import { useCallback, useEffect, useRef, useState } from "react";

/**
 * `useIdleTimeout` — Fires a warning after `warnAfterMs` of inactivity and
 * a hard `onTimeout` at `warnAfterMs + graceMs`.
 *
 * "Activity" = any of: mousemove, mousedown, keydown, scroll, touchstart,
 * or a call to the returned `reset()` (used by our API client on every
 * successful request so background polling doesn't bump the clock forward
 * but real user-driven fetches do).
 *
 * Timers are paused when the tab is hidden and restarted from zero when
 * the tab regains focus — otherwise a laptop closed on the coffee table
 * would appear "active" for hours.
 *
 * The hook is single-instance safe (multiple consumers reset the same
 * timer) because we install one global listener set on mount.
 */
const DEFAULT_ACTIVITY_EVENTS = [
    "mousemove",
    "mousedown",
    "keydown",
    "scroll",
    "touchstart",
    "click",
];

export default function useIdleTimeout({
    enabled = true,
    warnAfterMs = 27 * 60 * 1000,   // 27 min → show warning modal
    graceMs = 3 * 60 * 1000,        // 3 min grace → then hard logout
    onWarn,
    onTimeout,
} = {}) {
    const [isWarning, setIsWarning] = useState(false);
    const [remainingMs, setRemainingMs] = useState(graceMs);
    const warnTimer = useRef(null);
    const timeoutTimer = useRef(null);
    const tickTimer = useRef(null);

    const clearAll = useCallback(() => {
        if (warnTimer.current) clearTimeout(warnTimer.current);
        if (timeoutTimer.current) clearTimeout(timeoutTimer.current);
        if (tickTimer.current) clearInterval(tickTimer.current);
        warnTimer.current = null;
        timeoutTimer.current = null;
        tickTimer.current = null;
    }, []);

    const startTimers = useCallback(() => {
        clearAll();
        warnTimer.current = setTimeout(() => {
            setIsWarning(true);
            setRemainingMs(graceMs);
            const startedAt = Date.now();
            tickTimer.current = setInterval(() => {
                const left = Math.max(0, graceMs - (Date.now() - startedAt));
                setRemainingMs(left);
            }, 1000);
            if (typeof onWarn === "function") onWarn();
            timeoutTimer.current = setTimeout(() => {
                clearAll();
                setIsWarning(false);
                if (typeof onTimeout === "function") onTimeout();
            }, graceMs);
        }, warnAfterMs);
    }, [clearAll, graceMs, onTimeout, onWarn, warnAfterMs]);

    const reset = useCallback(() => {
        if (!enabled) return;
        // If the warning is already up, `reset()` alone should not silently
        // dismiss it — the user must click "Stay signed in". This prevents
        // background API calls from suppressing the modal without consent.
        if (isWarning) return;
        startTimers();
    }, [enabled, isWarning, startTimers]);

    const stayActive = useCallback(() => {
        setIsWarning(false);
        startTimers();
    }, [startTimers]);

    // Install / uninstall global activity listeners.
    useEffect(() => {
        if (!enabled) {
            clearAll();
            setIsWarning(false);
            return undefined;
        }
        const handleActivity = () => reset();
        DEFAULT_ACTIVITY_EVENTS.forEach((ev) =>
            window.addEventListener(ev, handleActivity, { passive: true }),
        );
        const onVis = () => {
            if (document.visibilityState === "visible") {
                reset();
            } else {
                clearAll();
                setIsWarning(false);
            }
        };
        document.addEventListener("visibilitychange", onVis);
        startTimers();
        return () => {
            DEFAULT_ACTIVITY_EVENTS.forEach((ev) =>
                window.removeEventListener(ev, handleActivity),
            );
            document.removeEventListener("visibilitychange", onVis);
            clearAll();
        };
        // We intentionally only depend on `enabled` so the listener set is
        // installed once per authenticated session. `reset` closes over
        // fresh state via its own useCallback dependencies.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled]);

    return { isWarning, remainingMs, stayActive };
}
